const SearchBarResponsive = styled.div`
  width: 160px;
  min-width: 0;

  input::placeholder {
    color: transparent;
  }

  @media (max-width: 1400px) {
    order: 2;
    flex: 1 1 auto;
    width: clamp(180px, 40vw, 800px);
    max-width: 800px;
    margin: 0 10px;

    input::placeholder {
      color: inherit;
    }
  }

  @media (max-width: 750px) {
    flex: 0 0 auto;
    width: auto;
    margin-left: auto;
  }
`
import { MouseEvent, useState } from 'react'
import styled from 'styled-components'
import { ExternalLink } from '@gnomad/ui'
import { useRecoilValue } from 'recoil'
import { experienceModeAtom, TopResultsTab } from './sharedState'
import { buildStateUrl, useAppNavigation } from './hooks/useAppNavigation'
import { shouldHandleSemanticLinkClick } from './navigationUrl'
import { PageHeadingRouterLink, PageHeadingExternalLink } from './UserInterface'
import { NewSearchBar } from './Searchbox'
import { Link } from 'react-router-dom'

// @ts-ignore
import AoULogo from './assets/AoU_Logo.svg'
// @ts-ignore
import NIHLogo from './assets/NIH_Logo.svg'
// @ts-ignore
import RegisterButton from './assets/register.svg'

interface PageHeadingWrapperProps {
  dropdownOpen: boolean;
}

const PageHeadingWrapper = styled.div<PageHeadingWrapperProps>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background-color: #262262;
  color: white;
  box-shadow: 0 4px 2px -2px gray;
  z-index: 11;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 10px 30px;

  .logos {
    display: flex;
    align-items: center;
    min-width: 0;

    & > * {
      margin-right: 20px;
    }
  }

  .nav-items {
    display: flex;
    align-items: center;
    min-width: 0;
    margin-left: 20px;

    /* Links Button for Mobile */
    .links-button {
      flex: 0 0 auto;
      background: none;
      border: none;
      color: white;
      font-size: 22px;
      font-weight: 900;
      cursor: pointer;
      display: none;

      &:hover {
        opacity: 0.7;
      }

      @media (max-width: 1400px) {
        display: block;
      }
    }

    /* Inline Links for Desktop */
    .inline-links {
      display: flex;

      a {
        color: white;
        padding: 4px 10px;
        text-decoration: none;
        font-size: 15px;
        font-weight: 900;
        margin-right: 10px;

        &:hover {
          background-color: #3e3e7e;
        }
      }

      @media (max-width: 1400px) {
        display: none;
      }
    }

    /* Dropdown Content for Mobile */
    .dropdown-content {
      display: ${({ dropdownOpen }) => (dropdownOpen ? 'block' : 'none')};
      position: absolute;
      top: 50px; /* Adjusted to appear below the header */
      right: 0px; /* Align with padding */
      background-color: #262262;
      min-width: 160px;
      box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
      z-index: 1;

      a {
        color: white;
        padding: 12px 16px;
        text-decoration: none;
        display: block;

        &:hover {
          background-color: #3e3e7e;
        }
      }

      @media (min-width: 1400px) {
        display: none;
      }
    }
  }

  .register {
    display: flex;
    align-items: center;

    @media (max-width: 1400px) {
      display: none;
    }

    & > a {
      display: flex;
      align-items: center;
      padding: 12px 16px;

      svg, img {
        height: 32px;
        width: auto;
      }

      &:hover {
        opacity: 0.7;
      }
    }
  }

  @media (max-width: 1400px) {
    flex-direction: row;
    justify-content: space-between;
    padding: 10px 20px;

    .logos {
      order: 1;
    }

    .nav-items {
      order: 3;
      margin-left: 0;
    }
  }

  @media (max-width: 500px) {
    padding: 8px 10px;

    .logos > * {
      margin-right: 10px;
    }
  }
`

const LogoItem = styled.div`
  display: flex;
  align-items: center;

  & + & {
    margin-left: 20px;
    margin-right: 20px;
  }

  svg, img {
    height: 32px;
    width: auto;

    @media (max-width: 1400px) {
      height: 26px;
    }
  }
`


const ResultsDropdownWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:hover .results-submenu,
  &:focus-within .results-submenu {
    display: block;
  }
`

const ResultsSubmenu = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  background-color: #262262;
  min-width: 180px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.3);
  z-index: 20;

  a {
    color: white;
    padding: 14px 16px;
    text-decoration: none;
    display: block;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;

    &:hover {
      background-color: #3e3e7e;
    }
  }
`

const resultsBaseState = {
  resultIndex: 'top-associations',
  resultLayout: 'full',
  geneId: null,
  regionId: null,
  variantId: null,
  analysisId: null,
}

const PageHeading = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const experienceMode = useRecoilValue(experienceModeAtom)
  const { goToResults: navigateToResults } = useAppNavigation()

  const resultsUrl = (tab: TopResultsTab = 'all-phenotypes') =>
    buildStateUrl({
      ...resultsBaseState,
      topResultsTab: tab,
      experienceMode,
      activeSurface: 'results',
    })

  const goToResults = (
    event: MouseEvent<HTMLAnchorElement>,
    tab: TopResultsTab = 'all-phenotypes'
  ) => {
    if (!shouldHandleSemanticLinkClick(event)) return
    event.preventDefault()
    navigateToResults(tab)
    closeDropdown()
  }

  const toggleDropdown = () => setDropdownOpen(!dropdownOpen)
  const closeDropdown = () => setDropdownOpen(false)

  return (
    <PageHeadingWrapper dropdownOpen={dropdownOpen}>
      <div className="logos">
        <ExternalLink href="https://www.nih.gov/">
          <LogoItem>
            <NIHLogo />
          </LogoItem>
        </ExternalLink>
        <ExternalLink href="https://allofus.nih.gov/" >
          <LogoItem>
            <AoULogo />
          </LogoItem>
        </ExternalLink>
      </div>
      <div className="nav-items">
        {/* Links Button for Mobile */}
        <button
          type="button"
          className="links-button"
          aria-label={dropdownOpen ? 'Close site navigation' : 'Open site navigation'}
          aria-expanded={dropdownOpen}
          aria-controls="site-navigation-menu"
          onClick={toggleDropdown}
        >
          <span aria-hidden="true">☰</span>
        </button>
        {/* Inline Links for Desktop */}
        <div className="inline-links">
          <PageHeadingRouterLink to="/" onClick={closeDropdown}>
            Home
          </PageHeadingRouterLink>
          <PageHeadingRouterLink to="/about" onClick={closeDropdown}>
            About
          </PageHeadingRouterLink>
          <PageHeadingRouterLink to="/walkthrough" onClick={closeDropdown}>
            Walkthrough
          </PageHeadingRouterLink>
          <PageHeadingRouterLink to="/faq" onClick={closeDropdown}>
            FAQ
          </PageHeadingRouterLink>
          <ResultsDropdownWrapper>
            <PageHeadingRouterLink
              to={resultsUrl()}
              aria-haspopup="menu"
              onClick={(event) => goToResults(event)}
            >
              Results
            </PageHeadingRouterLink>
            <ResultsSubmenu className="results-submenu" role="menu">
              <Link role="menuitem" to={resultsUrl('all-phenotypes')} onClick={(event) => goToResults(event, 'all-phenotypes')}>All Phenotypes</Link>
              <Link role="menuitem" to={resultsUrl('all-genes')} onClick={(event) => goToResults(event, 'all-genes')}>All Genes</Link>
              <Link role="menuitem" to={resultsUrl('gene-burden')} onClick={(event) => goToResults(event, 'gene-burden')}>Gene Burden</Link>
              <Link role="menuitem" to={resultsUrl('single-variants')} onClick={(event) => goToResults(event, 'single-variants')}>Single Variants</Link>
            </ResultsSubmenu>
          </ResultsDropdownWrapper>
          <PageHeadingExternalLink href="https://support.researchallofus.org/hc/en-us" onClick={closeDropdown}>
            Support
          </PageHeadingExternalLink>
        </div>
        {/* Dropdown Content for Mobile */}
        <div className="dropdown-content" id="site-navigation-menu">
          <PageHeadingRouterLink to="/" onClick={closeDropdown}>
            Home
          </PageHeadingRouterLink>
          <PageHeadingRouterLink to="/about" onClick={closeDropdown}>
            About
          </PageHeadingRouterLink>
          <PageHeadingRouterLink to="/walkthrough" onClick={closeDropdown}>
            Walkthrough
          </PageHeadingRouterLink>
          <PageHeadingRouterLink to="/faq" onClick={closeDropdown}>
            FAQ
          </PageHeadingRouterLink>

          <PageHeadingRouterLink to={resultsUrl()} onClick={(event) => goToResults(event)}>
            Results
          </PageHeadingRouterLink>
          <PageHeadingExternalLink href="https://support.researchallofus.org/hc/en-us" onClick={closeDropdown}>
            Support
          </PageHeadingExternalLink>
          {/* Register Link for Mobile */}
          <ExternalLink href="https://www.researchallofus.org/register">
            <RegisterButton style={{ height: 33 }} />
          </ExternalLink>
        </div>
      </div>
      {/* Keep one search instance mounted so only one modal can be active. */}
      <SearchBarResponsive><NewSearchBar compactOnMobile /></SearchBarResponsive>
      {/* Register Button for Desktop */}
      <div className="register">
        <ExternalLink href="https://www.researchallofus.org/register">
          <RegisterButton />
        </ExternalLink>
      </div>
    </PageHeadingWrapper>
  )
}

export default PageHeading

